import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/app/providers/AuthProvider";
import { updateUser } from "@/features/users/api/usersApi";
import type { UserData } from "@/shared/types/auth";

interface UpdateProfilePayload {
  name: string;
  phone: string;
}

export function resolvePortalUserId(u: UserData | null): string | undefined {
  if (!u) {
    return undefined;
  }
  const fromLocal = typeof u.localId === "string" && u.localId ? u.localId : undefined;
  const fromAlt =
    (typeof u.userId === "string" && u.userId) || (typeof u._id === "string" && u._id) || undefined;
  return fromLocal || fromAlt;
}

export function useUpdateCurrentUserProfile() {
  const queryClient = useQueryClient();
  const { user, setUser } = useAuth();

  return useMutation({
    mutationFn: async ({ name, phone }: UpdateProfilePayload) => {
      const userId = resolvePortalUserId(user);
      if (!userId) {
        throw new Error("Missing user id; cannot update profile.");
      }
      const trimmedName = name.trim();
      const trimmedPhone = phone.trim();
      return updateUser(userId, {
        ...(trimmedName ? { name: trimmedName } : {}),
        phone: trimmedPhone,
      });
    },
    onSuccess: (data) => {
      if (!user) {
        return;
      }
      const merged: UserData = {
        ...user,
        ...(data as Record<string, unknown>),
        email: user.email,
        localId: user.localId || resolvePortalUserId(user) || "",
      };
      localStorage.setItem("UserData", JSON.stringify(merged));
      setUser(merged);
      queryClient.invalidateQueries({ queryKey: ["merchant", "profile"] });
    },
  });
}
